import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card } from '@sundkortid/ui';
import { formatIsk } from '@sundkortid/shared';
import { api } from '../../api/client';

interface DashboardStats {
  activePasses: number;
  totalPassesSold: number;
  totalRedemptions: number;
  totalRevenue: number;
}

interface Subscriber {
  id: string;
  email: string;
  createdAt: string;
}

interface Supporter {
  id: string;
  name: string | null;
  createdAt: string;
}

const NAV_ITEMS = [
  { to: '/admin', label: 'Yfirlit', icon: '~' },
  { to: '/admin/users', label: 'Notendur', icon: '~' },
  { to: '/admin/pools', label: 'Sundlaugar', icon: '~' },
  { to: '/admin/passes', label: 'Kort', icon: '~' },
  { to: '/admin/pricing', label: 'Verðlagning', icon: '~' },
  { to: '/admin/audit', label: 'Aðgerðaskrá', icon: '~' },
] as const;

export function AdminNav() {
  const { pathname } = useLocation();

  return (
    <nav className="flex gap-1 overflow-x-auto pb-1">
      {NAV_ITEMS.map(({ to, label }) => {
        const active = pathname === to;
        return (
          <Link
            key={to}
            to={to}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              active
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

export function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [supporters, setSupporters] = useState<Supporter[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<DashboardStats>('/admin/stats/dashboard').catch(() => null),
      api.get<Subscriber[]>('/admin/waitlist/subscribers').catch(() => []),
      api.get<Supporter[]>('/admin/waitlist/supporters').catch(() => []),
    ]).then(([s, subs, sups]) => {
      setStats(s);
      setSubscribers(subs ?? []);
      setSupporters(sups ?? []);
    }).finally(() => setLoading(false));
  }, []);

  const quickLinks = [
    { to: '/admin/users', label: 'Notendur', desc: 'Skoða og breyta notendum, hlutverkum og aðgangi', color: 'bg-blue-50 border-blue-200 hover:border-blue-400' },
    { to: '/admin/pools', label: 'Sundlaugar', desc: 'Bæta við, breyta eða eyða sundlaugum', color: 'bg-cyan-50 border-cyan-200 hover:border-cyan-400' },
    { to: '/admin/passes', label: 'Kort', desc: 'Öll seld kort, stöður og afturkallanir', color: 'bg-green-50 border-green-200 hover:border-green-400' },
    { to: '/admin/pricing', label: 'Verðlagning', desc: 'Stýra verðum á mismunandi kortategundum', color: 'bg-amber-50 border-amber-200 hover:border-amber-400' },
    { to: '/admin/audit', label: 'Aðgerðaskrá', desc: 'Ferill allra aðgerða í kerfinu', color: 'bg-purple-50 border-purple-200 hover:border-purple-400' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col gap-4">
          <h1 className="text-xl font-title font-bold text-primary">Sundkortið — Stjórnborð</h1>
          <AdminNav />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-gray-500">Hleð gögnum...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <p className="text-sm text-gray-500">Virk kort</p>
              <p className="text-3xl font-bold text-primary">{stats?.activePasses ?? 0}</p>
            </Card>
            <Card>
              <p className="text-sm text-gray-500">Kort seld</p>
              <p className="text-3xl font-bold text-primary">{stats?.totalPassesSold ?? 0}</p>
            </Card>
            <Card>
              <p className="text-sm text-gray-500">Notkun samtals</p>
              <p className="text-3xl font-bold text-primary">{stats?.totalRedemptions ?? 0}</p>
            </Card>
            <Card>
              <p className="text-sm text-gray-500">Tekjur</p>
              <p className="text-3xl font-bold text-primary">{formatIsk(stats?.totalRevenue ?? 0)}</p>
            </Card>
          </div>
        )}

        <h2 className="text-lg font-title font-semibold text-gray-800 mb-4">Flýtileiðir</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map(({ to, label, desc, color }) => (
            <Link
              key={to}
              to={to}
              className={`block rounded-xl border-2 p-5 transition-all ${color}`}
            >
              <h3 className="font-semibold text-gray-900 mb-1">{label}</h3>
              <p className="text-sm text-gray-600">{desc}</p>
            </Link>
          ))}
        </div>

        {/* ── Waitlist & Supporters ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* Newsletter signups */}
          <div>
            <h2 className="text-lg font-title font-semibold text-gray-800 mb-4">
              Póstlisti ({subscribers.length})
            </h2>
            <Card>
              {subscribers.length === 0 ? (
                <p className="text-sm text-gray-500">Engir skráðir enn.</p>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 font-medium">Netfang</th>
                        <th className="pb-2 font-medium text-right">Dags.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((s) => (
                        <tr key={s.id} className="border-b last:border-0">
                          <td className="py-2 text-gray-800">{s.email}</td>
                          <td className="py-2 text-gray-500 text-right whitespace-nowrap">
                            {new Date(s.createdAt).toLocaleDateString('is-IS')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Undirskriftalisti */}
          <div>
            <h2 className="text-lg font-title font-semibold text-gray-800 mb-4">
              Undirskriftalisti ({supporters.length})
            </h2>
            <Card>
              {supporters.length === 0 ? (
                <p className="text-sm text-gray-500">Engir skráðir enn.</p>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 font-medium">Nafn</th>
                        <th className="pb-2 font-medium text-right">Dags.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supporters.map((s) => (
                        <tr key={s.id} className="border-b last:border-0">
                          <td className="py-2 text-gray-800">{s.name ?? 'Nafnlaust'}</td>
                          <td className="py-2 text-gray-500 text-right whitespace-nowrap">
                            {new Date(s.createdAt).toLocaleDateString('is-IS')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
